<template>
  <div>
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="800">
      <material-card
        :title="
          isNew
            ? $t('components.plancreationwizard.add_billing_import_term')
            : $t('components.plancreationwizard.edit_billing_import_term')
        "
        :color="$vuetify.theme.themes.light.secondary"
        icon="schedule"
      >
        <v-card-text v-if="dialogs.edit">
          <v-form ref="form" v-model="valid">
            <v-container fluid fill-height>
              <v-row wrap>
                <v-col cols="12" md="4">
                  <trans-select2
                    v-model="item"
                    propertyName="Employer"
                    :items="calculatedEmployers"
                    :label="$t('globals.employer')"
                    field="Name"
                    itemValue="Identifier"
                    class="required"
                    :rules="[$rules.required]"
                  ></trans-select2>
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="item.IsCustom"
                    :label="$t('headers.billingimportterm.custom')"
                  ></v-checkbox>
                </v-col>
                <template v-if="!item.IsCustom">
                  <v-col cols="12" md="4">
                    <pbas-date-picker
                      :label="$t('globals.start_date')"
                      :value="item.StartDate"
                      @update-date="item.StartDate = $event"
                      :rules="[rules.planEffectiveDate, $rules.required]"
                      :class="'required'"
                      :allowedDates="planEffectiveDates"
                    ></pbas-date-picker>
                  </v-col>
                  <v-col cols="12" md="12" class="pt-0 pb-0">
                    <period-picker
                      v-model="item"
                      :termLabel="$t('components.plancreationwizard.membereligibility.period')"
                      :frequencyLabel="$t('headers.billingimportterm.frequency')"
                      :rules="[$rules.required]"
                      class="required"
                      :timeunitTooltip="
                        $t('components.periodpicker.termunit_tooltip')
                      "
                      :frequencyTooltip="
                        $t('components.periodpicker.frequency_tooltip')
                      "
                    ></period-picker>
                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeBillingImportTerm">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            :disabled="!valid"
            color="primary"
            @click="saveBillingImportTermItem"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog v-model="dialogs.delete" scrollable persistent max-width="400">
      <material-card
        :title="$t('components.plancreationwizard.remove_billing_import_term')"
        :color="$vuetify.theme.themes.light.error"
        icon="remove"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="removeBillingImportTermItem">
            {{ $t("globals.remove") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <material-card
      :title="
        $t('components.plancreationwizard.billing_import_term_configuration')
      "
      :color="$vuetify.theme.themes.light.secondary"
      icon="schedule"
      style="margin-top:30px"
    >
      <v-card-text>
        <pbas-data-table
          ref="table"
          :dataProvider="provider"
          :rowActions="rowActions"
          :floatActions="floatActions"
          fileNamePrefix="billingImportTerm"
          identifier="billingImportTermTable"
          @error="error = $event"
          :showSelect="false"
        ></pbas-data-table>
      </v-card-text>
    </material-card>
    <contribution-categories
      :effectiveDate="effectiveDate"
      @update-contribution-categories="
        $emit('update-contribution-categories', $event)
      "
      :contributionCategories="contributionCategories"
      :employers="employers"
      :disabled="disabled"
      :hasStores="hasStores"
    />
    <v-dialog
      scrollable
      v-model="dialogs.optionalRequiredFields"
      persistent
      max-width="500px"
    >
      <material-card :title="requiredFieldsTitle" icon="add" :color="$vuetify.theme.themes.light.success">
        <v-card-text>
          <v-form>
            <v-container fluid grid-list-md>
              <v-row>
                <v-col xs="12">
                  <v-checkbox
                    v-model="requiredField"
                    :label="$t('validation.required')"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialogs.optionalRequiredFields = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="primary" @click="tryAddBillingFields">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <material-card
      :title="$t('components.plancreationwizard.billing_import_fields')"
      :color="$vuetify.theme.themes.light.secondary"
      icon="folder_shared"
      v-if="hasBillingImport"
    >
      <DualListBox
        :source="unselectedFieldsInternal"
        :destination="selectedFieldsInternal"
        label="Label"
        @onChangeList="onChangeList"
        :disabled="disabled"
      />
    </material-card>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DualListBox from "dual-listbox-vue";
import "dual-listbox-vue/dist/dual-listbox.css";
import ErrorHandler from "@/components/ErrorHandler";
import BillingImportTermHeaders from "@/headers/BillingImportTerm";
import { InMemoryProvider } from "pbas-vue-components";
import TransSelect2 from "@/components/TransSelect2";
import PeriodPicker from "@/components/PlanBuilder/PeriodPicker";
import ContributionCategories from "@/components/PlanBuilder/ContributionCategories";
export default {
  components: {
    DualListBox,
    ErrorHandler,
    TransSelect2,
    PeriodPicker,
    ContributionCategories,
  },
  props: [
    "selectedFields",
    "unselectedFields",
    "billingTerm",
    "effectiveDate",
    "employers",
    "hasBillingImport",
    "disabled",
    "contributionCategories",
    "hasStores",
  ],
  data() {
    return {
      sourceFields: [],
      destinationFields: [],
      selectedFieldsInternal: [],
      unselectedFieldsInternal: [],
      item: null,
      floatActions: [
        {
          name: "addBillingImportTerm",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => {
            this.item = {
              Identifier: Math.random().toString(),
              Term: {},
            };
            this.isNew = true;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
      ],
      rowActions: [
        {
          name: "editBillingImportTerm",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.item = JSON.parse(JSON.stringify(item));
            this.isNew = false;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
        {
          name: "removeBillingImportTerm",
          icon: "delete",
          title: "globals.remove",
          handler: (item) => {
            this.item = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
          visible: () => !this.disabled,
        },
      ],
      provider: undefined,
      headers: BillingImportTermHeaders,
      error: null,
      isNew: false,
      requiredField: false,
      valid: false,
      dialogs: {
        optionalRequiredFields: false,
        edit: false,
        delete: false,
      },
      rules: {
        planEffectiveDate: (v) => {
          if (v === undefined || v === null || v === "") {
            return true;
          }
          if (this.effectiveDate === null || this.effectiveDate === undefined) {
            return true;
          }
          let planEffectiveTimestamps = Date.parse(this.effectiveDate);

          const planEffectiveDate = new Date(planEffectiveTimestamps);
          if (!this.isDate(v)) {
            return this.$t("validation.date_{0}", [
              this.formatDate(planEffectiveDate),
            ]);
          }
          return (
            this.planEffectiveDates(v) ||
            this.$t("validation.date_{0}", [this.formatDate(planEffectiveDate)])
          );
        },
      },
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
    calculatedEmployers() {
      if (this.isNew) {
        return this.employers.filter(
          (o) =>
            this.provider.items.filter(
              (e) => e.Employer.Identifier === o.Identifier
            ).length === 0
        );
      } else {
        return this.employers.filter(
          (o) =>
            this.provider.items.filter(
              (e) => e.Employer.Identifier === o.Identifier
            ).length === 0 || this.item.Employer.Identifier === o.Identifier
        );
      }
    },
    requiredFieldsTitle() {
      if (
        this.unselectedFieldsInternal.length - this.sourceFields.length ===
        1
      ) {
        return this.$t(
          "components.plancreationwizard.select_optional_or_required_field"
        );
      } else {
        return this.$t(
          "components.plancreationwizard.select_optional_or_required_field_plural"
        );
      }
    },
  },
  watch: {
    selectedFields: {
      handler(v) {
        this.selectedFieldsInternal = v;
      },
    },
    unselectedFields: {
      handler(v) {
        this.unselectedFieldsInternal = v;
      },
    },
    billingTerm: {
      handler(v) {
        if (this.provider !== null && this.provider !== undefined) {
          this.provider.items = v;
          this.$refs.table.getDataAsync();
        } else {
          this.provider = new InMemoryProvider(v, this.headers);
        }
      },
    },
    "item.IsCustom": {
      handler(v) {
        if (v) {
          delete this.item.TimeUnit;
          delete this.item.Frequency;
          delete this.item.StartDate;
        }
      },
    },
    hasBillingImport: {
      handler(v) {
        if (!v) {
          this.$emit("update-selection", []);
        }
      },
    },
  },
  mounted() {
    if (this.billingTerm !== null && this.billingTerm !== undefined) {
      this.provider = new InMemoryProvider(this.billingTerm, this.headers);
    }
    this.selectedFieldsInternal = this.selectedFields;
    this.unselectedFieldsInternal = this.unselectedFields;
  },
  methods: {
    isDate(val) {
      if (
        new RegExp(/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/).test(
          val
        )
      ) {
        return !isNaN(Date.parse(val));
      }
      return false;
    },
    formatDate(d) {
      let month = (d.getUTCMonth() + 1).toString();
      if (month.length === 1) {
        month = "0" + month;
      }
      let day = d.getUTCDate().toString();
      if (day.length === 1) {
        day = "0" + day;
      }
      return `${d.getUTCFullYear()}-${month}-${day}`;
    },
    planEffectiveDates(currentDateString) {
      const currentTimestamps = Date.parse(currentDateString);
      const currentDate = new Date(currentTimestamps);
      let planEffectiveTimestamps = Date.parse(this.effectiveDate);

      const planEffectiveDate = new Date(planEffectiveTimestamps);

      return currentDate >= planEffectiveDate;
    },
    saveBillingImportTermItem() {
      let item = JSON.parse(JSON.stringify(this.item));
      item.EffectiveDate = this.effectiveDate;
      if (item.IsCustom !== true) {
        delete item.Term;
      }
      if (this.isNew) {
        this.addBillingImportTermItem(item);
      } else {
        this.updateBillingImportTermItem(item);
      }
    },
    updateBillingImportTermItem(item) {
      let billingImportTermItem;
      billingImportTermItem = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.provider.items[
        this.provider.items.indexOf(billingImportTermItem)
      ] = item;

      this.closeBillingImportTerm();
      this.$refs.table.getDataAsync();
      this.$emit("update-billing-term", this.provider.items);
    },
    addBillingImportTermItem(item) {
      this.provider.items.push(item);
      this.closeBillingImportTerm();
      this.$refs.table.getDataAsync();
      this.$emit("update-billing-term", this.provider.items);
    },
    removeBillingImportTermItem() {
      let item = JSON.parse(JSON.stringify(this.item));
      let billingImportTermItem;
      billingImportTermItem = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.provider.items.splice(
        this.provider.items.indexOf(billingImportTermItem),
        1
      );
      this.closeBillingImportTerm();
      this.$refs.table.getDataAsync();
      this.$emit("update-billing-term", this.provider.items);
    },
    closeBillingImportTerm() {
      this.dialogs.edit = false;
      this.dialogs.delete = false;
      this.item = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    onChangeList: function({ source, destination }) {
      if (this.unselectedFieldsInternal.length > source.length) {
        this.sourceFields = source;
        this.destinationFields = destination;
        this.requiredField = false;
        this.dialogs.optionalRequiredFields = true;
      } else {
        this.unselectedFieldsInternal = source.map((o) => {
          o.Label = o.Label.split("(")[0].trim();
          delete o.IsOptional;
          return o;
        });
        this.selectedFieldsInternal = destination;
        this.$emit("update-selection", this.selectedFieldsInternal);
      }
    },
    tryAddBillingFields() {
      this.unselectedFieldsInternal = this.sourceFields;
      let destinationFields = this.destinationFields.map((o) => {
        const name = this.requiredField
          ? `${o.Label} (required)`
          : `${o.Label} (optional)`;
        o.IsOptional =
          o.Label.indexOf("(") === -1 ? !this.requiredField : o.IsOptional;
        o.Label = o.Label.indexOf("(") === -1 ? name : o.Label;
        return o;
      });
      this.selectedFieldsInternal = destinationFields;
      this.dialogs.optionalRequiredFields = false;
      this.$emit("update-selection", this.selectedFieldsInternal);
    },
  },
};
</script>

<style></style>
